/**
 * Client-side avatar pre-processing (AC-E5). We validate + downscale in the
 * browser before upload so we never ship a 12 MP phone photo over the wire for
 * a 256² avatar; the server still re-encodes and bounds authoritatively.
 */

/** Raw upload cap mirrored from the backend (5 MB) — reject bigger before decode. */
export const AVATAR_MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/** Accepted input types (sniffed server-side too; this is the first gate). */
export const ACCEPTED_AVATAR_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;

/** Longest edge (px) we downscale to before upload — server bounds to 256² after. */
const CLIENT_MAX_EDGE = 512;

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to decode image'));
    img.src = src;
  });
}

/**
 * Downscale a picked image to a bounded square-ish webp Blob. Falls back to the
 * original file if the canvas pipeline is unavailable (the server still bounds
 * it), so this never blocks a legitimate upload.
 */
export async function downscaleImageFile(
  file: File,
  maxEdge = CLIENT_MAX_EDGE,
): Promise<Blob> {
  try {
    const img = await loadImage(await readAsDataUrl(file));
    const longest = Math.max(img.width, img.height) || 1;
    const scale = Math.min(1, maxEdge / longest);
    const width = Math.max(1, Math.round(img.width * scale));
    const height = Math.max(1, Math.round(img.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/webp', 0.9),
    );
    return blob ?? file;
  } catch {
    // Any decode/canvas failure → let the server handle the raw file.
    return file;
  }
}

/**
 * Validate a picked file against the accepted-type + size gates. Returns an
 * error message string when rejected, or `null` when acceptable.
 */
export function validateAvatarFile(file: File): string | null {
  if (!ACCEPTED_AVATAR_TYPES.includes(file.type as (typeof ACCEPTED_AVATAR_TYPES)[number])) {
    return 'Unsupported file type. Upload a PNG, JPEG, or WebP image.';
  }
  if (file.size > AVATAR_MAX_UPLOAD_BYTES) {
    return 'Image is larger than 5 MB. Choose a smaller file.';
  }
  return null;
}
